import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { ParsedResults, SearchPageProps } from '../../../pages/courses/[[...parts]]';
import BlueprintIcon from '../../../components/BlueprintIcon';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import { ModalContents } from '../../../components/Modal/ModalContents';
import { CourseSortBySelector, EducatorSortBySelector } from './SortBySelector';
import styles from './styles.module.scss';
import { UrlObject, useRouterStatePart } from '../../../hooks/useRouterState';
import Badges from './Badges';
import { SearchIndexSortBy } from '../../../services/search/shared';
import Link from 'next/link';
import { SearchIndexAutoComplete } from '@shared/types';
import { GeneralSearch } from '../../home/SearchBar/PrimaryQuerySelect';
import FilterListRegularIcon from '../../../components/Icons/FilterListRegularIcon';

export type HeaderProps = {
    title: string;
    filters: SearchPageProps['query']['filters'];
    setFilters: (newFilters: SearchPageProps['query']['filters']) => void;
    getFilterLink: (newFilters: SearchPageProps['query']['filters']) => UrlObject;
    results: {
        courses: ParsedResults['courses'];
        onlineAlternativeCourses: ParsedResults['onlineAlternativeCourses'];
        educators: ParsedResults['educators'];
    };
    query: SearchPageProps['query'];
    providers_only: boolean;
    search: string;
    setSearch: (newSearch: SearchIndexAutoComplete | GeneralSearch) => void;
    sortBy: SearchIndexSortBy;
    setSortBy: (newSort: SearchIndexSortBy) => void;
    filtersContent: JSX.Element;
};

const Header: FunctionComponent<HeaderProps> = ({
    title,
    filters,
    setFilters,
    getFilterLink,
    results,
    providers_only,
    search,
    setSearch,
    sortBy,
    setSortBy,
    filtersContent,
}) => {
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const filterButtonRef = useRef<HTMLButtonElement>(null);

    const [courseFilters, setCourseFilters, getCourseFilterChangeLink] = useRouterStatePart(
        filters,
        setFilters,
        getFilterLink,
        'courseFilters'
    );

    const onFilterModalClose = useCallback(() => {
        filterButtonRef.current?.focus();
        setIsFilterModalOpen(false);
    }, []);

    const courseResultCount = useMemo(
        () => results.courses.totalResults,
        [results.courses.totalResults]
    );
    const educatorResultCount = useMemo(
        () => results.educators.totalResults,
        [results.educators.totalResults]
    );
    const alternativeResultCount = useMemo(
        () => results.onlineAlternativeCourses.totalResults,
        [results.onlineAlternativeCourses]
    );

    const resultCount = useMemo(() => {
        return providers_only ? educatorResultCount : courseResultCount;
    }, [courseResultCount, educatorResultCount, providers_only]);

    const resultCountDisplay = useMemo(() => {
        if (resultCount === 0) {
            return '';
        }

        return resultCount;
    }, [resultCount]);

    const resetSearch = useCallback(() => {
        setSearch({
            type: 'search',
            name: '',
        });
    }, [setSearch]);

    return (
        <div className={styles.headerCard}>
            <div className={styles.heading}>
                <h1>
                    {resultCountDisplay} {title}
                </h1>
                <div className={styles.HeaderCardActions}>
                    <Button
                        className={styles.filterButton}
                        onClick={() => setIsFilterModalOpen(true)}
                        ref={filterButtonRef}
                    >
                        <FilterListRegularIcon />
                        Filter
                    </Button>
                    {providers_only ? (
                        <EducatorSortBySelector currentSortByValue={sortBy} setSortBy={setSortBy} />
                    ) : (
                        <CourseSortBySelector currentSortByValue={sortBy} setSortBy={setSortBy} />
                    )}
                </div>
                {isFilterModalOpen ? (
                    <Modal onClose={onFilterModalClose} size="medium">
                        <ModalContents
                            heading="Filters"
                            onClose={onFilterModalClose}
                            contents={filtersContent}
                            footer={
                                <Button
                                    className={styles.showResultsButton}
                                    preset="primary"
                                    onClick={onFilterModalClose}
                                >
                                    Show results
                                </Button>
                            }
                        />
                    </Modal>
                ) : null}
            </div>

            <Badges
                className={styles.Badges}
                filters={courseFilters}
                setFilters={setCourseFilters}
                getFilterLink={getCourseFilterChangeLink}
                search={search}
                resetSearch={resetSearch}
            />

            <NoResults
                count={resultCount}
                educatorResultCount={educatorResultCount}
                alternativeResultCount={alternativeResultCount}
            />
        </div>
    );
};

type NoResultsProps = {
    count: number;
    educatorResultCount: number;
    alternativeResultCount: number;
};

const NoResults: FunctionComponent<NoResultsProps> = ({
    count,
    educatorResultCount,
    alternativeResultCount,
}) => {
    const suggestionText = useMemo(() => {
        if (alternativeResultCount > 0 && educatorResultCount > 0) {
            return 'Explore our online options and related educators below to see if they help you.';
        }
        if (alternativeResultCount > 0) {
            return 'Explore our online options below.';
        }
        if (educatorResultCount > 0) {
            return 'Explore the educators below who might be able to help you.';
        }
        return '';
    }, [alternativeResultCount, educatorResultCount]);

    if (count > 0) {
        return null;
    }

    return (
        <>
            <p>We couldn&#39;t find any listings for your search.&nbsp;{suggestionText}</p>
            <p className={styles.teach_this_msg}>
                Know someone teaching this?{' '}
                <Link href="/add-educator">Help them become an Educator</Link> on Cademy.
            </p>
        </>
    );
};

export default Header;
